import { keepPreviousData } from "@tanstack/react-query";
import { RowSelectionState } from "@tanstack/react-table";
import { useApiFilters, useFiltersV2MetaAutoUpdate } from "PFApp/components/filters_v2";
import { useStorageTableColumns } from "PFApp/hooks/use_storage_table_columns";
import useStorage from "PFCore/helpers/use_storage";
import { useProfiles } from "PFCore/hooks/queries/profiles/use_profiles";
import { OrderOption } from "PFTypes";
import { useEffect, useMemo, useRef, useState } from "react";

import { useProfilesColumns } from "../hooks/use_profiles_columns";
import { ViewMode } from "../profiles_page.types";
import { ProfilesPageContext } from "./profiles_page_context";

const LS_EXPIRY_TIME_SECONDS = 60 * 60;

export const ProfilesPageContextProvider = ({ children }) => {
  const [order, setOrder] = useStorage<OrderOption | null>("profilesPageOrder", null, {
    expiresIn: LS_EXPIRY_TIME_SECONDS
  });
  const [viewMode, setViewMode] = useStorage<ViewMode>("profilesPageViewMode", ViewMode.Cards, {
    expiresIn: LS_EXPIRY_TIME_SECONDS
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<RowSelectionState>({});

  const { defaultColumns, customFieldsColumns } = useProfilesColumns();
  const { selectedColumns, setSelectedColumns } = useStorageTableColumns(
    `profiles_columns`,
    defaultColumns.concat(customFieldsColumns),
    defaultColumns
  );

  const { apiFilters, areApiFiltersReady } = useApiFilters();

  const { data, isFetching, error } = useProfiles(
    {
      filters: apiFilters,
      order: order ?? undefined,
      perPage: 20,
      nested: true,
      highlights: true,
      page: currentPage
    },
    { placeholderData: keepPreviousData, enabled: areApiFiltersReady }
  );

  const scrollableContainerRef = useRef<HTMLDivElement>(null);

  useFiltersV2MetaAutoUpdate(data?.meta);

  useEffect(() => {
    setCurrentPage(1);
  }, [apiFilters]);

  const profiles = data?.entries ?? [];
  const meta = useMemo(() => data?.meta ?? { total: 0, page: 0, perPage: 0, totalPages: 0 }, [data?.meta]);

  return (
    <ProfilesPageContext.Provider
      value={{
        profiles,
        meta,
        isFetching: isFetching || !areApiFiltersReady,
        error,
        order,
        setOrder,
        viewMode,
        selectedIds,
        setSelectedIds,
        selectedColumns,
        setSelectedColumns,
        setViewMode,
        setCurrentPage,
        scrollableContainerRef
      }}
    >
      {children}
    </ProfilesPageContext.Provider>
  );
};
